import { Fragment, FunctionalComponent, getAssetPath, h } from '@stencil/core'
import { JSXBase, VNode } from '@stencil/core/internal'
import { createStore } from '@stencil/store'
import { noop } from 'lodash-es'
import { InertiaLink } from 'src/inertia/link/link'
import { ColorsType } from 'src/tailwind'
import { copyText } from 'src/utils/clipboard'

import { IBookmark, IHighlightReel, ISalesforceField } from '../../../../bookmarks/types'
import { INoteClip, IQuestionClip } from '../../../../call/types'
import { mapBookmarkToField, unmapBookmarkToField } from '../../../../crm/salesforce/state'
import { SalesforceMenu } from '../../../../customize/bookmarks/ui'
import { IconName } from '../../../../icons'
import { Menu, MenuItem } from '../../../../ui/menu/menu'
import { classes, colorClasses } from '../../../../utils/css'
import { useStoredValue } from '../../../../utils/storage'
import { hasMatchingNote } from '../annotation'
import { CallStore, getShareableShareUrl, PlaybackStore, TranscriptStore } from '../state'

type ClipboardProvider = {
  name: string
  iconUrl: string
}

declare global {
  interface LocalStorageKeys {
    actionItemsClipboardProvider: ClipboardProvider
    aiNoteClipboardProvider: ClipboardProvider
  }
}

export const clipMinHeight = '24px'
export const clipDimensionalStyle = { minHeight: clipMinHeight }

export const actionItemProviders = [
  { name: 'Asana', iconUrl: getAssetPath('./assets/app-icons/asana.svg') },
  { name: 'Google Docs', iconUrl: getAssetPath('./assets/app-icons/docs.svg') },
  { name: 'GMail', iconUrl: getAssetPath('./assets/app-icons/gmail.svg') },
  { name: 'Todoist', iconUrl: getAssetPath('./assets/app-icons/todoist.svg') },
  { name: 'Microsoft Word', iconUrl: getAssetPath('./assets/app-icons/word.svg') },
]

export const aiNoteProviders = [
  { name: 'Google Docs', iconUrl: getAssetPath('./assets/app-icons/docs.svg') },
  { name: 'GMail', iconUrl: getAssetPath('./assets/app-icons/gmail.svg') },
  { name: 'Notion', iconUrl: getAssetPath('./assets/app-icons/notion.png') },
  { name: 'Microsoft Word', iconUrl: getAssetPath('./assets/app-icons/word.svg') },
]

export const ClipboardStore = createStore({
  actionItemsProvider: null as ClipboardProvider,
  aiNoteProvider: null as ClipboardProvider,
})

ClipboardStore.use(useStoredValue(ClipboardStore, 'actionItemsClipboardProvider', 'actionItemsProvider'))
ClipboardStore.use(useStoredValue(ClipboardStore, 'aiNoteClipboardProvider', 'aiNoteProvider'))

const ClipboardTarget: FunctionalComponent<
  { provider: ClipboardProvider } & Omit<Parameters<typeof MenuItem>[0], 'icon'>
> = ({ provider, class: className = '', onClick = noop, ...attrs }) => (
  <MenuItem
    {...attrs}
    icon={() => <img class='object-contain w-4 h-4' src={provider.iconUrl} />}
    class={`
      ${className} flex w-full px-3 py-1 items-center spacing-x-2 hover:bg-surface-upper transition duration-200 select-none cursor-pointer
    `}
    onClick={onClick}
  >
    {provider.name}
  </MenuItem>
)

type ClipboardMenu = FunctionalComponent<
  Parameters<typeof Menu>[0] & { onProviderSelected: (p: ClipboardProvider) => void }
>

export const ActionItemsClipboardMenu: ClipboardMenu = ({ onProviderSelected, ...attrs }) => {
  return (
    <Menu {...attrs}>
      {actionItemProviders.map((p) => (
        <ClipboardTarget
          close-tooltip
          provider={p}
          onClick={() => {
            ClipboardStore.state.actionItemsProvider = p
            onProviderSelected(p)
          }}
        />
      ))}
    </Menu>
  )
}

export const AiNoteClipboardMenu: ClipboardMenu = ({ onProviderSelected, ...attrs }) => {
  return (
    <Menu {...attrs}>
      {aiNoteProviders.map((p) => (
        <ClipboardTarget
          close-tooltip
          provider={p}
          onClick={() => {
            ClipboardStore.state.aiNoteProvider = p
            onProviderSelected(p)
          }}
        />
      ))}
    </Menu>
  )
}

export function copyClipUrl(clip: INoteClip) {
  const getPromise = getShareableShareUrl(clip.note).then((url) => ({ plain_text: url }))
  return copyText(getPromise, true)
}

export function copyHighlightReelUrl(highlightReel: IHighlightReel) {
  const getPromise = getShareableShareUrl(highlightReel).then((url) => ({ plain_text: url }))
  return copyText(getPromise, true)
}

export function playClip(seekTime: number, seekTrigger: string, seekId: number) {
  PlaybackStore.state.paused = true
  PlaybackStore.state.seekTrigger = seekTrigger
  PlaybackStore.state.seekId = seekId
  PlaybackStore.state.seekTime = seekTime
  PlaybackStore.state.paused = false
}

// true if
// 1. accessed from attendee link
// 2. accessed from full access link, viewer is not a teammate or the host and the url is not yet activated
export function hideClipShareUrl(shareUrl: string): boolean {
  return CallStore.state.access === 'external_view_only' || (!shareUrl && CallStore.state.readonly)
}

export const NoteSection: FunctionalComponent<{
  bookmark?: IBookmark
  label?: string
  color?: ColorsType
  fields?: ISalesforceField[]
  numSalesforceContacts?: number
  callId?: number
  renderHeaderContent?: () => VNode
}> = (
  { bookmark = {} as IBookmark, fields, numSalesforceContacts, callId, color, label, renderHeaderContent = noop },
  children
) => {
  return (
    <section class='flex flex-col w-full' key={bookmark.id}>
      <div class='flex spacing-x-1'>
        <SectionHeader
          class={`w-full mb-1 ${classes(bookmark.action_item, '-mt-1')}
          ${colorClasses({ text: color || bookmark.hex_color })}`}
        >
          {label || bookmark.label}
          {numSalesforceContacts > 0 && fields.length > 0 && bookmark.highlight && (
            <SalesforceMenu
              bookmark={bookmark}
              tooltipAnchor='right'
              onSelect={(field) => {
                mapBookmarkToField(field, bookmark, callId)
              }}
              onRemove={(field) => {
                unmapBookmarkToField(field, null)
              }}
              renderButton={(inactive) => (
                <button
                  class={`ml-2 flex spacing-x-1 items-center uppercase ${classes(
                    inactive,
                    'hover:text-link text-surface-higher',
                    'text-link'
                  )}`}
                >
                  <ui-icon icon={inactive ? 'cloud-plus' : 'cloud-arrow-up'} />
                  <span class='text-xs font-bold'>{inactive ? 'Link' : 'Linked'}</span>
                </button>
              )}
            />
          )}
          {renderHeaderContent()}
        </SectionHeader>
      </div>
      {children.length ? (
        <ul class='flex flex-col'>{children}</ul>
      ) : (
        <div class='mt-2 text-xs font-bold leading-xl text-center text-surface-higher uppercase rounded border border-surface-high'>
          None
        </div>
      )}
    </section>
  )
}

export function scrollToClip(clip: INoteClip | IQuestionClip) {
  const scrollCue = TranscriptStore.state.cues.find((cue) => {
    if ('note' in clip) {
      return hasMatchingNote(cue, clip.note) || cue.start_time >= clip.note.start_time
    } else {
      return cue.id === clip.cue_id
    }
  })
  TranscriptStore.state.autoScroll = false
  TranscriptStore.state.scrollCue = scrollCue
}

export const PlaceholderClip: FunctionalComponent<{
  bookmark: IBookmark
  mockSeconds?: number
}> = ({ bookmark, mockSeconds = 60 }) => {
  return (
    <li class='flex items-center p-2 -mx-2 text-base leading-none spacing-x-2'>
      <ui-pie-loader class={`ml-2px ${colorClasses({ text: bookmark.hex_color })}`} mockSeconds={mockSeconds} />
      <span class={`select-none italic font-semibold opacity-50 ${colorClasses({ text: bookmark.hex_color })}`}>
        Processing…
      </span>
    </li>
  )
}

export const ActionIcon: FunctionalComponent<
  {
    icon: IconName
    iconClass?: string
    text?: string
    textClass?: string
    hoverTip?: string
    clickTip?: string
    colorCss?: string
  } & JSXBase.IntrinsicElements['button']
> = (
  {
    icon,
    iconClass = 'text-base m-auto',
    text,
    textClass = 'text-base font-bold',
    hoverTip,
    clickTip,
    class: className = '',
    disabled,
    ...attrs
  },
  children
) => {
  return (
    <button
      class={`
        flex-shrink-0 flex items-center justify-center transition duration-200
        ${className}
        ${classes(disabled, 'opacity-50')}
      `}
      {...attrs}
    >
      {hoverTip && <ui-tooltip>{hoverTip}</ui-tooltip>}
      {clickTip && (
        <ui-tooltip on='click' bgColor='info-ok'>
          {clickTip}
        </ui-tooltip>
      )}
      {text && <span class={textClass}>{text}</span>}
      <ui-icon class={iconClass} icon={icon} />
      {children}
    </button>
  )
}

export const QuestionNoteSection: FunctionalComponent<{
  label: string
  clips: IQuestionClip[]
}> = ({ label, clips }) => {
  if (clips.length > 0) {
    return <QuestionNoteSectionV1 label={label} clips={clips} />
  }
  return null
}

export const QuestionNoteSectionV1: FunctionalComponent<{
  label: string
  clips: IQuestionClip[]
}> = ({ label, clips }) => (
  <NoteSection label={label} color='surface-highest'>
    {clips.map((clip) => (
      <QuestionClip
        clip={clip}
        avatarUrl={clip.speaker.email === CallStore.state.call.host.email ? CallStore.state.call.host.avatar_url : null}
      />
    ))}
  </NoteSection>
)

export const QuestionNoteSectionV2: FunctionalComponent<{
  label: string
  clips: INoteClip[]
}> = ({ label, clips }) => (
  <NoteSection label={label} color='surface-highest'>
    {clips.map((clip) => (
      <call-detail-notes-note-clip
        key={`call-detail-notes-note-clip-${clip.id}`}
        clip={clip}
        showMenu={false}
        readonly={true}
        highlit={false}
      />
    ))}
  </NoteSection>
)

export const QuestionClip: FunctionalComponent<{
  clip: IQuestionClip
  avatarUrl: string
}> = ({ clip }) => (
  <li
    key={clip.cue_id}
    class='group flex flex-row p-1 w-full max-w-full text-xs font-medium leading-125% text-white hover:bg-white hover:bg-opacity-10 rounded-md cursor-pointer'
    onClick={() => playClip(clip.start_time, 'side-panel-question', clip.cue_id)}
    style={clipDimensionalStyle}
  >
    <ui-icon icon='question-circle' class='inline group-hover:hidden mx-1' size='base' />
    <ui-icon icon='play' class='hidden group-hover:inline mx-1' size='base' />
    <span>“{clip.question}”</span>
  </li>
)

export const LinkableText: FunctionalComponent<{
  content: string
}> = ({ content }) => {
  const regex = /^http(s)?:\/\//
  return (
    <span>
      {content.split(' ').map((part) =>
        regex.test(part) ? (
          <span>
            <a target='_blank' class='text-link hover:underline' href={part}>
              {part}
            </a>{' '}
          </span>
        ) : (
          <span>{part} </span>
        )
      )}
    </span>
  )
}

export const ChatMessage: FunctionalComponent<{
  id: number
  senderName: string
  content: string
  startTime: number
}> = ({ id, senderName, content, startTime }) => (
  <li
    key={id}
    class='group flex flex-row p-1 w-full max-w-full text-xs font-medium leading-125% text-white hover:bg-white hover:bg-opacity-10 rounded-md cursor-pointer'
    onClick={() => playClip(startTime, 'side-panel-chat-message', id)}
    style={clipDimensionalStyle}
  >
    <ui-icon icon='chat-left-text' class='inline group-hover:hidden mx-1' size='base' />
    <ui-icon icon='play' class='hidden group-hover:inline mx-1' size='base' />
    <span>
      {senderName}: <LinkableText content={content} />
    </span>
  </li>
)

export const SectionHeader: FunctionalComponent<{ icon?: IconName } & JSXBase.IntrinsicElements['h4']> = (
  { icon, class: className = '', style = {}, ...attrs },
  children
) => (
  <Fragment>
    <h4
      {...attrs}
      class={`${className} w-full relative flex items-center text-base font-extrabold tracking-wide uppercase spacing-x-1`}
      style={{ ...style }}
    >
      {icon && <ui-icon class='mr-8px text-3xl' icon={icon} />}
      {children}
    </h4>
  </Fragment>
)

export const IconButton: FunctionalComponent<
  { icon: IconName; iconClass?: string } & JSXBase.IntrinsicElements['button']
> = ({ icon, iconClass, class: className, ...attrs }, children) => (
  <button
    {...attrs}
    class={`
      ${className}
      flex items-center text-2xs uppercase font-bold opacity-100 cursor-pointer hover:text-link animate duration-200
    `}
  >
    <ui-icon class={iconClass} style={{ fontSize: '1.5em', marginRight: '0.5ch' }} icon={icon}></ui-icon>
    <span class='tracking-wide'>{children}</span>
  </button>
)

export const CallMenuButton: FunctionalComponent<JSXBase.IntrinsicElements['button'] & { useRightPadding?: boolean }> =
  ({ useRightPadding = true, class: className = '', disabled, ...attrs }, children) => (
    <button
      disabled={disabled}
      class={`
      ${className}
      flex items-center justify-between normal-case p-1 pl-2 text-base
      ${classes(useRightPadding, 'pr-2')}
      font-bold whitespace-no-wrap rounded-md bg-opacity-10
      ${classes(
        disabled,
        'bg-glyph-light text-glyph-light cursor-not-allowed',
        'text-link bg-link hover:text-surface-base hover:bg-link cursor-pointer'
      )}
    `}
      {...attrs}
    >
      {children}
    </button>
  )

export const AiActionItemBanner: FunctionalComponent<
  JSXBase.IntrinsicElements['div'] & { onDelete: () => void; deleted: boolean; customizeUrl: string }
> = ({ onDelete, deleted, customizeUrl, class: className = '', ...attrs }) => (
  <div
    class={`
      ${className}
      flex items-center gap-0.5 w-full text-sm
    `}
    {...attrs}
  >
    {deleted ? (
      <div class='flex flex-1 items-center py-2 px-9px font-medium text-info-warn bg-info-warn bg-opacity-10 rounded spacing-x-3px'>
        <span>✨</span>
        <span class='font-bold'>Deleted</span>
        <span>- Go to</span>
        <InertiaLink href={customizeUrl} class='underline'>
          Settings
        </InertiaLink>
        <span>to disable for future calls</span>
      </div>
    ) : (
      <Fragment>
        <div class='flex flex-1 items-center py-2 px-2 font-medium text-info-warn bg-info-warn bg-opacity-10 rounded-l'>
          ✨ Action Items Generated by AI
        </div>
        <button
          class='flex p-2 font-bold text-info-warn uppercase bg-info-warn bg-opacity-10 hover:bg-opacity-20 rounded-r'
          onClick={onDelete}
        >
          Delete All
        </button>
      </Fragment>
    )}
  </div>
)
